import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCollabAwarenessHistory } from "../../vsecurite/vsecuriteSlice";
import Langs, { Localize } from "../../../app/lang/langs";
import moment from "moment";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Fade from "@mui/material/Fade";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import CloseIcon from "@mui/icons-material/Close";

/* ── Brand / palette constants ── */
const ORANGE = "#FF9015";
const ORANGE_LIGHT = "rgba(255,144,21,0.08)";
const BORDER_SUBTLE = "rgba(0,0,0,0.06)";
const TEXT_PRIMARY = "#111318";
const TEXT_SECONDARY = "#4b5263";
const TEXT_MUTED = "#8b92a0";
const BG_GRADIENT = "linear-gradient(160deg, #f7f8fa 0%, #eef0f4 50%, #e8eaef 100%)";
const SURFACE = "rgba(255,255,255,0.90)";
const GREEN = "#16a34a";
const EASE = "cubic-bezier(0.4,0,0.2,1)";

/* ── CSS keyframe injection ── */
const KEYFRAMES = `
    @keyframes awarenessHistoryFadeInUp {
        from { opacity: 0; transform: translateY(12px); }
        to   { opacity: 1; transform: translateY(0); }
    }
`;

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function AwarenessHistory() {
    const dispatch = useDispatch();

    /* ── Redux state selectors ── */
    const historyList = useSelector((state) => state.vsecurite.awareness.collabHistory);
    const loading = useSelector((state) => state.vsecurite.awareness.loading);
    const lang = useSelector((state) => state.settings.lang);
    const isMobile = useSelector((state) => state.settings.isMobile);

    const mobile = isMobile === 2;

    const [selectedItem, setSelectedItem] = useState(null);

    /* ── Fetch history on component mount ── */
    useEffect(() => {
        dispatch(fetchCollabAwarenessHistory());
    }, [dispatch]);

    /**
     * sortedHistory
     * Returns a copy of historyList sorted by acknowledged_at descending (most recent first).
     * Recalculates only when historyList reference changes.
     */
    const sortedHistory = useMemo(() => {
        if (!historyList || historyList.length === 0) return [];
        return [...historyList].sort((a, b) => {
            const dateA = moment(a.acknowledged_at);
            const dateB = moment(b.acknowledged_at);
            /* Descending: more recent first */
            return dateB.valueOf() - dateA.valueOf();
        });
    }, [historyList]);

    /**
     * formatDate
     * Formats an ISO date string to the DD/MM/YYYY HH:mm display format.
     * Returns a dash if the date is invalid or missing.
     * @param {string} dateStr - ISO date string
     * @returns {string} Formatted date string
     */
    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        const m = moment(dateStr);
        return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "—";
    };

    /* ── Loading state ── */
    if (loading && (!historyList || historyList.length === 0)) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 280,
                    background: BG_GRADIENT,
                    borderRadius: "20px",
                    mt: 0,
                }}
            >
                <CircularProgress sx={{ color: ORANGE }} size={38} thickness={3.5} />
            </Box>
        );
    }

    return (
        <>
            <style>{KEYFRAMES}</style>

            <Box
                sx={{
                    background: BG_GRADIENT,
                    borderRadius: "20px",
                    p: { xs: 1.5, sm: 3 },
                    maxHeight: "calc(100vh - 280px)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    mt: 0,
                    animation: `awarenessHistoryFadeInUp 450ms ${EASE} both`,
                }}
            >
                {/* ── Section header ── */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            background: ORANGE_LIGHT,
                            border: `1px solid rgba(255,144,21,0.14)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <HistoryRoundedIcon sx={{ color: ORANGE, fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: mobile ? "1rem" : "1.1rem",
                                fontWeight: 650,
                                color: TEXT_PRIMARY,
                                letterSpacing: "-0.02em",
                                fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                lineHeight: 1.2,
                            }}
                        >
                            <Langs str="VSEC_HISTORY_TITLE" />
                        </Typography>
                        {sortedHistory.length > 0 && (
                            <Typography
                                sx={{
                                    fontSize: "0.75rem",
                                    color: TEXT_MUTED,
                                    fontWeight: 500,
                                    fontFamily: "'Inter',sans-serif",
                                }}
                            >
                                {sortedHistory.length} {sortedHistory.length > 1 ? Localize("VSEC_HISTORY_COUNT_PLURAL", lang) : Localize("VSEC_HISTORY_COUNT_SINGULAR", lang)}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                {/* ── Empty state ── */}
                {sortedHistory.length === 0 && (
                    <Fade in timeout={400}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1.5,
                                minHeight: 200,
                                borderRadius: "14px",
                                background: SURFACE,
                                border: `1px solid ${BORDER_SUBTLE}`,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: "16px",
                                    background: "rgba(0,0,0,0.03)",
                                    border: `1px solid ${BORDER_SUBTLE}`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <InboxRoundedIcon sx={{ fontSize: 28, color: TEXT_MUTED }} />
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: "0.9rem",
                                    color: TEXT_MUTED,
                                    fontWeight: 500,
                                    fontFamily: "'Inter',sans-serif",
                                    textAlign: "center",
                                }}
                            >
                                <Langs str="VSEC_HISTORY_EMPTY" />
                            </Typography>
                        </Box>
                    </Fade>
                )}

                {/* ── History list ── */}
                {sortedHistory.length > 0 && (
                    <Box
                        sx={{
                            background: SURFACE,
                            borderRadius: "16px",
                            border: `1px solid ${BORDER_SUBTLE}`,
                            overflowY: "auto",
                            flex: 1,
                            minHeight: 0,
                            "&::-webkit-scrollbar": { width: "5px" },
                            "&::-webkit-scrollbar-track": { background: "transparent" },
                            "&::-webkit-scrollbar-thumb": {
                                background: "rgba(255,144,21,0.20)",
                                borderRadius: "3px",
                            },
                            "&::-webkit-scrollbar-thumb:hover": {
                                background: "rgba(255,144,21,0.35)",
                            },
                        }}
                    >
                        <List disablePadding>
                            {sortedHistory.map((item, idx) => (
                                <React.Fragment key={item.id}>
                                    <Fade in timeout={350 + idx * 60}>
                                        <ListItem
                                            alignItems="flex-start"
                                            onClick={() => setSelectedItem(item)}
                                            sx={{
                                                px: { xs: 1.5, sm: 2.5 },
                                                py: 1.8,
                                                cursor: "pointer",
                                                minHeight: mobile ? 64 : "auto",
                                                transition: `background 250ms ${EASE}`,
                                                animation: `awarenessHistoryFadeInUp 400ms ${EASE} ${idx * 60}ms both`,
                                                "&:hover": {
                                                    background: "rgba(255,144,21,0.05)",
                                                },
                                                "&:active": mobile ? { background: "rgba(255,144,21,0.08)" } : {},
                                                flexDirection: mobile ? "column" : "row",
                                                alignItems: mobile ? "flex-start" : "flex-start",
                                                gap: mobile ? 1 : 0,
                                            }}
                                        >
                                            {/* ── Green check icon on the left ── */}
                                            {!mobile && (
                                                <Box
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: "9px",
                                                        background: "rgba(22,163,74,0.08)",
                                                        border: "1px solid rgba(22,163,74,0.14)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        flexShrink: 0,
                                                        mr: 2,
                                                        mt: 0.3,
                                                    }}
                                                >
                                                    <CheckCircleRoundedIcon
                                                        sx={{ color: GREEN, fontSize: 17 }}
                                                    />
                                                </Box>
                                            )}

                                            {/* ── Campaign title and approval date ── */}
                                            {mobile ? (
                                                <Box sx={{ width: "100%" }}>
                                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                                        <Box
                                                            sx={{
                                                                width: 28,
                                                                height: 28,
                                                                borderRadius: "8px",
                                                                background: "rgba(22,163,74,0.08)",
                                                                border: "1px solid rgba(22,163,74,0.14)",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 15 }} />
                                                        </Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.9rem",
                                                                fontWeight: 600,
                                                                color: TEXT_PRIMARY,
                                                                fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                                                lineHeight: 1.35,
                                                                flex: 1,
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {item.title}
                                                        </Typography>
                                                    </Stack>
                                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.75rem",
                                                                color: TEXT_MUTED,
                                                                fontFamily: "'Inter',sans-serif",
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            {formatDate(item.acknowledged_at)}
                                                        </Typography>
                                                        <Chip
                                                            label={Localize("VSEC_CHIP_READ_APPROVED", lang)}
                                                            size="small"
                                                            icon={
                                                                <CheckCircleRoundedIcon
                                                                    sx={{
                                                                        fontSize: "12px !important",
                                                                        color: `${GREEN} !important`,
                                                                    }}
                                                                />
                                                            }
                                                            sx={{
                                                                background: "rgba(22,163,74,0.08)",
                                                                color: GREEN,
                                                                border: "1px solid rgba(22,163,74,0.18)",
                                                                borderRadius: "8px",
                                                                fontWeight: 600,
                                                                fontSize: "0.68rem",
                                                                fontFamily: "'Inter',sans-serif",
                                                                height: 24,
                                                                "& .MuiChip-label": { px: 0.75 },
                                                                "& .MuiChip-icon": { ml: 0.5 },
                                                            }}
                                                        />
                                                    </Stack>
                                                </Box>
                                            ) : (
                                                <>
                                                    <ListItemText
                                                        primary={
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "0.92rem",
                                                                    fontWeight: 600,
                                                                    color: TEXT_PRIMARY,
                                                                    fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                                                    letterSpacing: "-0.01em",
                                                                    lineHeight: 1.35,
                                                                    mb: 0.5,
                                                                }}
                                                            >
                                                                {item.title}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    fontSize: "0.78rem",
                                                                    color: TEXT_MUTED,
                                                                    fontFamily: "'Inter',sans-serif",
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {Localize("VSEC_HISTORY_APPROVED_ON", lang)} {formatDate(item.acknowledged_at)}
                                                            </Typography>
                                                        }
                                                    />

                                                    {/* ── "Lu & Approuvé" green chip ── */}
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            ml: 1.5,
                                                            flexShrink: 0,
                                                            alignSelf: "center",
                                                        }}
                                                    >
                                                        <Chip
                                                            label={Localize("VSEC_CHIP_READ_APPROVED", lang)}
                                                            size="small"
                                                            icon={
                                                                <CheckCircleRoundedIcon
                                                                    sx={{
                                                                        fontSize: "14px !important",
                                                                        color: `${GREEN} !important`,
                                                                    }}
                                                                />
                                                            }
                                                            sx={{
                                                                background: "rgba(22,163,74,0.08)",
                                                                color: GREEN,
                                                                border: "1px solid rgba(22,163,74,0.18)",
                                                                borderRadius: "8px",
                                                                fontWeight: 600,
                                                                fontSize: "0.72rem",
                                                                fontFamily: "'Inter',sans-serif",
                                                                height: 26,
                                                                "& .MuiChip-label": { px: 1 },
                                                                "& .MuiChip-icon": { ml: 0.5 },
                                                            }}
                                                        />
                                                    </Box>
                                                </>
                                            )}
                                        </ListItem>
                                    </Fade>

                                    {/* Divider between items, not after the last one */}
                                    {idx < sortedHistory.length - 1 && (
                                        <Divider
                                            sx={{
                                                mx: mobile ? 1.5 : 2.5,
                                                borderColor: BORDER_SUBTLE,
                                            }}
                                        />
                                    )}
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                )}
            </Box>

            {/* ── Detail dialog ── */}
            <Dialog
                open={selectedItem !== null}
                onClose={() => setSelectedItem(null)}
                maxWidth={mobile ? undefined : "sm"}
                fullWidth={!mobile}
                fullScreen={mobile}
                TransitionComponent={mobile ? SlideUp : undefined}
                PaperProps={{
                    sx: {
                        borderRadius: mobile ? 0 : "20px",
                        background: "#ffffff",
                        boxShadow: mobile ? "none" : "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)",
                    },
                }}
                sx={{ zIndex: 1400 }}
            >
                {selectedItem && (
                    <>
                        {mobile ? (
                            <AppBar
                                position="sticky"
                                elevation={0}
                                sx={{
                                    bgcolor: "#fff",
                                    color: TEXT_PRIMARY,
                                    borderBottom: "1px solid rgba(0,40,128,0.06)",
                                }}
                            >
                                <Toolbar>
                                    <IconButton edge="start" onClick={() => setSelectedItem(null)} sx={{ color: "rgba(0,40,128,0.7)" }}>
                                        <CloseIcon />
                                    </IconButton>
                                    <Typography
                                        sx={{
                                            ml: 1,
                                            fontWeight: 700,
                                            fontSize: "1rem",
                                            flex: 1,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {selectedItem.title}
                                    </Typography>
                                </Toolbar>
                            </AppBar>
                        ) : (
                            <DialogTitle sx={{ pt: 3, pb: 1, px: 3 }}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Box sx={{
                                        width: 36, height: 36, borderRadius: "10px",
                                        background: "rgba(255,144,21,0.08)",
                                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                    }}>
                                        <TaskAltRoundedIcon sx={{ color: ORANGE, fontSize: 20 }} />
                                    </Box>
                                    <Typography sx={{
                                        fontWeight: 650, fontSize: "1.05rem", color: TEXT_PRIMARY,
                                        letterSpacing: "-0.02em", fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                    }}>
                                        {selectedItem.title}
                                    </Typography>
                                </Stack>
                            </DialogTitle>
                        )}

                        <DialogContent sx={{ px: mobile ? 2 : 3, pb: 1, pt: mobile ? 2 : undefined }}>
                            {/* Acknowledged date */}
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 2 }}>
                                <CalendarTodayRoundedIcon sx={{ fontSize: 14, color: TEXT_MUTED }} />
                                <Typography sx={{ fontSize: "0.78rem", color: TEXT_MUTED, fontFamily: "'Inter',sans-serif", fontWeight: 500 }}>
                                    {Localize("VSEC_HISTORY_APPROVED_ON", lang)} {formatDate(selectedItem.acknowledged_at)}
                                </Typography>
                            </Stack>

                            <Divider sx={{ mb: 2, borderColor: "rgba(0,0,0,0.06)" }} />

                            {/* Description */}
                            {selectedItem.description && (
                                <Typography
                                    component="div"
                                    sx={{ fontSize: "0.9rem", color: TEXT_SECONDARY, fontFamily: "'Inter',sans-serif", lineHeight: 1.6, mb: 2, whiteSpace: "pre-wrap" }}
                                    dangerouslySetInnerHTML={{ __html: selectedItem.description }}
                                />
                            )}

                            {/* Content */}
                            {selectedItem.content && (
                                <Box sx={{
                                    maxHeight: mobile ? "none" : 320, overflowY: "auto", p: 2,
                                    borderRadius: "12px", background: "rgba(0,0,0,0.025)",
                                    border: "1px solid rgba(0,0,0,0.06)",
                                    "&::-webkit-scrollbar": { width: "5px" },
                                    "&::-webkit-scrollbar-track": { background: "transparent" },
                                    "&::-webkit-scrollbar-thumb": { background: "rgba(255,144,21,0.20)", borderRadius: "3px" },
                                    "&::-webkit-scrollbar-thumb:hover": { background: "rgba(255,144,21,0.35)" },
                                }}>
                                    <Typography
                                        component="div"
                                        sx={{ fontSize: "0.88rem", color: TEXT_SECONDARY, fontFamily: "'Inter',sans-serif", lineHeight: 1.7, wordBreak: "break-word", whiteSpace: "pre-wrap" }}
                                        dangerouslySetInnerHTML={{ __html: selectedItem.content }}
                                    />
                                </Box>
                            )}
                        </DialogContent>

                        <DialogActions
                            sx={{
                                px: mobile ? 2 : 3,
                                pb: 2.5,
                                pt: 2,
                                ...(mobile && {
                                    position: "sticky",
                                    bottom: 0,
                                    bgcolor: "#fff",
                                    borderTop: "1px solid rgba(0,40,128,0.06)",
                                }),
                            }}
                        >
                            <Button
                                onClick={() => setSelectedItem(null)}
                                fullWidth={mobile}
                                sx={{
                                    borderRadius: "10px", px: 2.5, py: 0.9, textTransform: "none",
                                    fontWeight: 600, fontSize: "0.88rem", color: TEXT_MUTED,
                                    minHeight: 48,
                                    fontFamily: "'Inter',sans-serif", border: "1px solid rgba(0,0,0,0.06)",
                                    "&:hover": { background: "rgba(0,0,0,0.04)" },
                                }}
                            >
                                <Langs str="VSEC_BTN_CLOSE" />
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </>
    );
}
